import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { databaseService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

const BLOG_COLLECTION = 'blogs';

export default function SerialManagement({ onBack }) {
  const [allBlogs, setAllBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(BLOG_COLLECTION);
      if (result.success) {
        const blogs = result.data.documents;
        
        // Sort all blogs by serialNumber
        const sortedAll = blogs
          .map(blog => ({
            ...blog,
            serialNumber: blog.serialNumber || 999999
          }))
          .sort((a, b) => a.serialNumber - b.serialNumber);
        
        // Sort featured blogs by featuredSerialNumber
        const sortedFeatured = blogs
          .filter(blog => blog.featured === true)
          .map(blog => ({
            ...blog,
            featuredSerialNumber: blog.featuredSerialNumber || 999999
          }))
          .sort((a, b) => a.featuredSerialNumber - b.featuredSerialNumber);
        
        setAllBlogs(sortedAll);
        setFeaturedBlogs(sortedFeatured);
      }
    } catch (error) {
      showToast('Failed to load blogs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const moveUp = (list, setList, index, field) => {
    if (index === 0) return;
    const newList = [...list];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setList(newList);
  };

  const moveDown = (list, setList, index, field) => {
    if (index === list.length - 1) return;
    const newList = [...list];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setList(newList);
  };

  const saveAllBlogsOrder = async () => {
    setSaving(true);
    try {
      // Update serialNumber for each blog
      for (let i = 0; i < allBlogs.length; i++) {
        await databaseService.updateDocument(
          BLOG_COLLECTION,
          allBlogs[i].$id,
          { serialNumber: i + 1 }
        );
      }
      showToast('All blogs order saved successfully', 'success');
    } catch (error) {
      showToast('Failed to save order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveFeaturedBlogsOrder = async () => {
    setSaving(true);
    try {
      // Update featuredSerialNumber for each featured blog
      for (let i = 0; i < featuredBlogs.length; i++) {
        await databaseService.updateDocument(
          BLOG_COLLECTION,
          featuredBlogs[i].$id,
          { featuredSerialNumber: i + 1 }
        );
      }
      showToast('Featured blogs order saved successfully', 'success');
    } catch (error) {
      showToast('Failed to save order', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p>Loading blogs...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6" style={{ color: '#4C1D95' }} />
        </button>
        <h1 className="text-3xl font-bold" style={{ color: '#4C1D95' }}>
          Serial Management
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* All Blogs Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold" style={{ color: '#4C1D95' }}>
              All Blogs Order ({allBlogs.length})
            </h2>
            <button
              onClick={saveAllBlogsOrder}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
              style={{ backgroundColor: '#4C1D95' }}
            >
              <Save className="w-4 h-4" />
              Save Order
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            This order will be reflected on the /blogs page
          </p>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {allBlogs.map((blog, index) => (
              <div
                key={blog.$id}
                className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveUp(allBlogs, setAllBlogs, index)}
                    disabled={index === 0}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveDown(allBlogs, setAllBlogs, index)}
                    disabled={index === allBlogs.length - 1}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-center gap-3 flex-1">
                  <span className="text-lg font-bold text-gray-400 min-w-[30px]">
                    {index + 1}
                  </span>
                  <img
                    src={blog.thumbnailUrl}
                    alt={blog.title}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold line-clamp-1">{blog.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: '#4C1D95' }}
                      >
                        {blog.category}
                      </span>
                      {blog.featured && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-400 text-yellow-900">
                          Featured
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Blogs Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold" style={{ color: '#4C1D95' }}>
              Featured Blogs Order ({featuredBlogs.length})
            </h2>
            <button
              onClick={saveFeaturedBlogsOrder}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
              style={{ backgroundColor: '#4C1D95' }}
            >
              <Save className="w-4 h-4" />
              Save Order
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            This order will be reflected on the homepage
          </p>

          {featuredBlogs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No featured blogs yet. Mark blogs as featured to manage their order here.
            </p>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {featuredBlogs.map((blog, index) => (
                <div
                  key={blog.$id}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveUp(featuredBlogs, setFeaturedBlogs, index)}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveDown(featuredBlogs, setFeaturedBlogs, index)}
                      disabled={index === featuredBlogs.length - 1}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-lg font-bold text-gray-400 min-w-[30px]">
                      {index + 1}
                    </span>
                    <img
                      src={blog.thumbnailUrl}
                      alt={blog.title}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold line-clamp-1">{blog.title}</h3>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full text-white inline-block mt-1"
                        style={{ backgroundColor: '#4C1D95' }}
                      >
                        {blog.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
